import useAudioBufferStore from '@/hooks/useAudioBufferStore';
import useUndoManager from '@/hooks/useUndoManager/useUndoManager';
import { Grey } from '@/styles/Colors';
import {
  FunctionComponent,
  SVGProps,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as CutIcon } from '../assets/icons/CutIcon.svg';
import { ReactComponent as RedoIcon } from '../assets/icons/RedoIcon.svg';
import { ReactComponent as SaveIcon } from '../assets/icons/SaveIcon.svg';
import { ReactComponent as TrimIcon } from '../assets/icons/TrimIcon.svg';
import { ReactComponent as UndoIcon } from '../assets/icons/UndoIcon.svg';
import ButtonGroup from '../Button/ButtonGroup';
import IconButton from '../Button/IconButton';
import { Tool, ToolbarAction } from './types';

interface ToolbarProps {
  isEditable?: boolean;
  isRangeSelected?: boolean;
  cutAudio?: () => void;
  trimAudio?: () => void;
  onSave?: () => void;
}

/**
 * ISSUE-060 — the icon AND the i18n key that names it, per toolbar action.
 *
 * There is exactly one `<IconButton>` below, inside a `.map()`, so the
 * accessible name has to be derived from the action. Keying this table by the
 * `ToolbarAction` union makes a sixth action a COMPILE error here instead of a
 * sixth silently nameless button in the DOM.
 *
 * Routes that guard does NOT cover (`Toolbar.a11y.test.tsx` covers what can be
 * covered at all):
 *   1. An icon-only control in an AudioEditor component this table never
 *      reaches — AudioEditor.tsx, Editor.tsx, Editor/*, TimeDisplay.tsx,
 *      SaveModal.tsx. A second `<IconButton>` added to THIS file is not blind:
 *      `Toolbar.a11y.test.tsx`'s `expectNamedButtons` helper pins the button
 *      COUNT as well as the names, and covers both toolbar shapes and both zoom
 *      components, so a sixth rendered button fails it. The table's own reach is
 *      the buttons rendered through the one `tools.map()` below.
 *   2. A present-but-WRONG label (`undo: { …, label: 'Redo' }`) — the type only
 *      demands a string. The a11y suite's click tests pin each name to the
 *      handler it fires, which is what catches a swap.
 *   3. A label with no entry in `en.json`: i18next returns the key itself, so
 *      the name stays non-empty English and only the translation is lost. The
 *      a11y suite asserts the keys are present in en.json for exactly that.
 *   4. The 28 icon-only `<IconButton>` sites elsewhere in src/, which
 *      ISSUE-060 defers and enumerates in its PR. Count them with the anchored
 *      grep in Toolbar.a11y.test.tsx — a bare `'<IconButton'` pattern also
 *      matches `<IconButtonProps>` and over-reports by one.
 */
const TOOLBAR_ITEMS: Record<
  ToolbarAction,
  { Icon: FunctionComponent<SVGProps<SVGSVGElement>>; label: string }
> = {
  undo: { Icon: UndoIcon, label: 'Undo' },
  redo: { Icon: RedoIcon, label: 'Redo' },
  cut: { Icon: CutIcon, label: 'Cut selection' },
  trim: { Icon: TrimIcon, label: 'Trim to selection' },
  save: { Icon: SaveIcon, label: 'Save edited audio' },
};

const Toolbar = ({
  isEditable = false,
  isRangeSelected = false,
  cutAudio,
  trimAudio,
  onSave,
}: ToolbarProps) => {
  const { t } = useTranslation();
  const { undo, redo, isUndoable, isRedoable, currentSnapshot } =
    useUndoManager();
  const [isBufferChanged, setIsBufferChanged] = useState(false);
  const { findAudioBufferIndex } = useAudioBufferStore();

  const tools: Tool[][] = useMemo(
    () => [
      [
        {
          action: 'undo',
          disabled: !isUndoable,
          onClick: () => undo(),
        },
        { action: 'redo', disabled: !isRedoable, onClick: () => redo() },
      ],
      ...(cutAudio && trimAudio && onSave
        ? [
            [
              {
                action: 'cut',
                disabled: !isRangeSelected,
                onClick: () => cutAudio(),
              } as Tool,
              {
                action: 'trim',
                disabled: !isRangeSelected,
                onClick: () => trimAudio(),
              } as Tool,
              {
                action: 'save',
                // trim 또는 cut을 했을 때만 저장 가능
                disabled: !isBufferChanged,
                onClick: () => onSave(),
              } as Tool,
            ],
          ]
        : []),
    ],
    [
      cutAudio,
      isRangeSelected,
      isRedoable,
      isUndoable,
      redo,
      trimAudio,
      undo,
      onSave,
      isBufferChanged,
    ]
  );

  useEffect(() => {
    if (!currentSnapshot) {
      setIsBufferChanged(false);
      return;
    }
    // trim이나 cut으로 audioBuffer 변경이 있을 때 설정
    const bufferIndex = findAudioBufferIndex(currentSnapshot.audioBufferId);
    setIsBufferChanged(bufferIndex > 0);
  }, [currentSnapshot, findAudioBufferIndex]);

  return (
    <ButtonGroup className="sup-audio-toolbar" radius="none">
      {tools.map((tool, index) => (
        <ButtonGroup key={index}>
          {tool.map((toolItem, index) => {
            const { Icon, label } = TOOLBAR_ITEMS[toolItem.action];
            return (
              <IconButton
                key={index}
                color={Grey[800]}
                variant="contained"
                // The icon carries no text, so this is the button's only
                // accessible name. It stays outside `tools` (a useMemo that
                // does not depend on `t`) so a language change re-reads it.
                aria-label={t(label) as string}
                disabled={!isEditable || toolItem.disabled}
                onClick={toolItem.onClick}
              >
                <Icon />
              </IconButton>
            );
          })}
        </ButtonGroup>
      ))}
    </ButtonGroup>
  );
};

export default Toolbar;
